import { FC, useEffect, useRef } from "react";
import useTrackingUserEngagement from "../hooks/useTrackingUserEngagement";
import useTrackingRecent from "../hooks/useTrackingRecent";
import { type Period } from "../utils/period";
import BarChart from "../components/BarChart/BarChart";

interface Props {
	userId: string;
	period: Period;
	onClose: () => void;
}

const UserProfilePanel: FC<Props> = ({ userId, period, onClose }) => {
	const engagement = useTrackingUserEngagement({
		userId,
		from: period.from,
		to: period.to
	});
	const recent = useTrackingRecent({ userId, limit: 25 });

	// Focus management: send initial focus to the close button on
	// mount, restore focus to whatever was focused before the dialog
	// opened on unmount, and trap Tab/Shift+Tab inside the panel while
	// it's open. Without the trap, keyboard users can Tab from the
	// panel into the page behind it — which contradicts `aria-modal`
	// and leaves the user navigating content the dialog visually
	// covers.
	const closeButtonRef = useRef<HTMLButtonElement | null>(null);
	const dialogRef = useRef<HTMLDivElement | null>(null);
	const previouslyFocusedRef = useRef<HTMLElement | null>(null);

	useEffect(() => {
		previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
		closeButtonRef.current?.focus();
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				e.stopPropagation();
				onClose();
				return;
			}
			if (e.key !== "Tab") return;
			// Focus trap. Find every tabbable child of the panel,
			// excluding disabled controls and elements with
			// `tabindex="-1"`. If focus is on the first one and the
			// user Shift+Tabs, jump to the last; if on the last and
			// they Tab, jump to the first. Anything else falls through
			// to native browser behaviour.
			const root = dialogRef.current;
			if (!root) return;
			const tabbables = Array.from(
				root.querySelectorAll<HTMLElement>(
					'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
				)
			).filter((el) => !el.hasAttribute("aria-hidden"));
			if (tabbables.length === 0) return;
			const first = tabbables[0];
			const last = tabbables[tabbables.length - 1];
			const active = document.activeElement;
			if (e.shiftKey && active === first) {
				e.preventDefault();
				last?.focus();
			} else if (!e.shiftKey && active === last) {
				e.preventDefault();
				first?.focus();
			}
		};
		document.addEventListener("keydown", onKeyDown);
		return () => {
			document.removeEventListener("keydown", onKeyDown);
			previouslyFocusedRef.current?.focus?.();
		};
	}, [onClose]);

	return (
		<div
			ref={dialogRef}
			className="fixed inset-0 z-40 flex"
			role="dialog"
			aria-modal="true"
			aria-label={`Engagement profile for ${userId}`}
		>
			{/* Backdrop is purely visual + click-to-dismiss. Aria-hidden
			    keeps it out of the keyboard tab order — the visible
			    Close button is the keyboard-accessible dismiss target. */}
			<div
				className="flex-1 bg-black/30"
				onClick={onClose}
				aria-hidden="true"
			/>
			<aside className="w-full max-w-xl h-full bg-white border-l border-border shadow-xl overflow-y-auto p-6">
				<div className="flex items-start justify-between mb-4">
					<div>
						<h2 className="font-heading text-lg font-semibold text-foreground">
							{engagement.data?.full_name ?? userId}
						</h2>
						<p className="text-sm text-muted-foreground">
							{engagement.data?.job_title ?? ""}
							{engagement.data?.org_team_level_one
								? ` · ${engagement.data.org_team_level_one}`
								: ""}
						</p>
					</div>
					<button
						type="button"
						ref={closeButtonRef}
						onClick={onClose}
						className="text-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:rounded"
					>
						Close
					</button>
				</div>

				{engagement.isLoading ? (
					<div className="h-32 animate-pulse bg-zinc-100 rounded" />
				) : engagement.isError ? (
					<p className="text-sm text-rose-600">Failed to load engagement.</p>
				) : engagement.data ? (
					<>
						<div className="grid grid-cols-3 gap-4 mb-6 text-sm">
							<div>
								<p className="text-muted-foreground">Total events</p>
								<p className="font-heading text-xl">
									{engagement.data.total_events.toLocaleString()}
								</p>
							</div>
							<div>
								<p className="text-muted-foreground">First in window</p>
								<p className="font-heading text-xs">
									{engagement.data.first_seen_in_window?.slice(0, 10) ?? "—"}
								</p>
							</div>
							<div>
								<p className="text-muted-foreground">Last in window</p>
								<p className="font-heading text-xs">
									{engagement.data.last_seen_in_window?.slice(0, 10) ?? "—"}
								</p>
							</div>
						</div>
						<div className="flex items-baseline justify-between mb-2 gap-2">
							<h3 className="font-heading font-medium text-sm">
								Event breakdown
							</h3>
							<p className="text-xs text-muted-foreground">
								Showing top{" "}
								{Math.min(
									12,
									engagement.data.event_breakdown.length
								).toLocaleString()}{" "}
								of {engagement.data.event_breakdown.length.toLocaleString()}
							</p>
						</div>
						<BarChart
							ariaLabel="User event breakdown"
							data={engagement.data.event_breakdown.slice(0, 12).map((e) => ({
								label: e.event_name,
								value: e.count
							}))}
							valueLabel="events"
						/>
					</>
				) : null}

				<div className="flex items-baseline justify-between mt-6 mb-2 gap-2">
					<h3 className="font-heading font-medium text-sm">Recent activity</h3>
					{recent.data ? (
						<p className="text-xs text-muted-foreground">
							{recent.data.length.toLocaleString()}{" "}
							{recent.data.length === 1 ? "event" : "events"}
						</p>
					) : null}
				</div>
				{recent.isLoading ? (
					<div className="h-24 animate-pulse bg-zinc-100 rounded" />
				) : recent.data && recent.data.length > 0 ? (
					<ul className="space-y-1.5 text-xs">
						{recent.data.map((row) => (
							<li
								key={row.id}
								className="font-mono border-b border-border py-1"
							>
								<span className="text-muted-foreground">
									{row.created_at.replace("T", " ").slice(0, 19)}
								</span>{" "}
								<span className="text-foreground">{row.event_name}</span>
								{row.entity_id ? (
									<span className="text-muted-foreground">
										{" "}
										· {row.entity_id}
									</span>
								) : null}
							</li>
						))}
					</ul>
				) : (
					<p className="text-sm text-muted-foreground">No recent activity.</p>
				)}
			</aside>
		</div>
	);
};

export default UserProfilePanel;
